import { api } from '../lib/api.js';
import { WEBSITE } from '../lib/edition.js';
import { CloudSyncButton } from './CloudGames.js';
import { useEffect, useState } from 'react';

import { WORKSPACE_TABS, tabCommandId, type WorkspaceTabId } from '@ai-studio/shared';

import { dockPanel, externalizePanel, floatPanel, groupOf, primaryGroup, removePanel } from '../lib/dock-model.js';
import { useKeyHint, useShortcutVersion, keyHint, runCommand } from '../lib/shortcuts/index.js';
import { eventSocket, type SocketStatus } from '../lib/ws.js';
import { useAuth } from '../state/auth.js';
import { WORKSPACE_PRESETS, useLayout } from '../state/layout.js';
import { usePlay } from '../state/play.js';
import { useWorkspace } from '../state/workspace.js';
import { openSearch } from './CommandPalette.js';
import { loadLayoutFromFile, openSettings, revertLayoutsToFactory, saveLayoutAs, saveLayoutToFile } from './SettingsView.js';
import { ContextMenu, MenuButton, type MenuItem } from './ui/controls.js';
import { Icon, type IconName } from './ui/icons.js';
import { panelDragProps } from './ui/layout.js';
import { ExportDialog } from './ExportDialog.js';

const TAB_ICONS: Record<string, IconName> = {
  scene: 'scene',
  model: 'model',
  pixel: 'pixel',
  sound: 'audio',
  video: 'camera',
  play: 'playOutline',
  code: 'code',
  board: 'board',
};

/** Top bar tabs, in the shared order (packages/shared WORKSPACE_TABS). */
export const TABS: ReadonlyArray<{ id: WorkspaceTabId; label: string; icon: IconName; key: string }> = WORKSPACE_TABS.map((t) => ({
  id: t.id,
  label: t.label,
  icon: TAB_ICONS[t.id] ?? 'file',
  key: t.key,
}));

/** Split / float / close actions for a view, shared by tab context menus. */
export function viewDockMenu(view: string): MenuItem[] {
  const layout = useLayout.getState();
  const dock = layout.dock;
  const target = groupOf(dock, view)?.id ?? primaryGroup(dock)?.id;
  const items: MenuItem[] = [];
  if (target) {
    items.push(
      { id: 'right', label: 'Open in Right Split', icon: 'panelRight', run: () => layout.set(dockPanel(dock, view, primaryGroup(dock)?.id ?? target, 'right', 40)) },
      { id: 'down', label: 'Open in Bottom Split', icon: 'panelBottom', run: () => layout.set(dockPanel(dock, view, primaryGroup(dock)?.id ?? target, 'bottom', 40)) },
    );
  }
  items.push({ id: 'float', label: 'Float', icon: 'copy', run: () => layout.set(floatPanel(dock, view, { x: 120, y: 60, w: 640, h: 440 })) });
  if (['board', 'settings'].includes(view))
    items.push({ id: 'window', label: 'Open in New Window', icon: 'fullscreen', run: () => layout.set(externalizePanel(dock, view)) });
  if (groupOf(dock, view) && !groupOf(dock, view)?.primary)
    items.push({ id: 'home', label: 'Move Back to Main Area', icon: 'layers', run: () => layout.set(dockPanel(dock, view, primaryGroup(dock)!.id, 'center')) });
  items.push({ id: 'sep', label: '', separator: true }, { id: 'hide', label: 'Close View', icon: 'x', run: () => layout.set(removePanel(dock, view)) });
  return items;
}

function layoutMenu(): MenuItem[] {
  const layout = useLayout.getState();
  const items: MenuItem[] = [{ id: 'h', label: 'Workspaces', heading: true }];
  for (const p of WORKSPACE_PRESETS) {
    items.push({
      id: `preset-${p.id}`,
      label: p.label,
      icon: layout.preset === p.id ? 'check' : undefined,
      shortcut: keyHint(`layout.preset.${p.id}`) || undefined,
      run: () => runCommand(`layout.preset.${p.id}`),
    });
  }
  const saved = Object.keys(layout.saved);
  if (saved.length) {
    items.push({ id: 'sep-saved', label: '', separator: true }, { id: 'h-saved', label: 'Saved layouts', heading: true });
    for (const name of saved) items.push({ id: `saved-${name}`, label: name, icon: 'layers', run: () => layout.load(name) });
  }
  items.push(
    { id: 'sep', label: '', separator: true },
    { id: 'save', label: 'Save Layout…', icon: 'plus', run: () => void saveLayoutAs() },
    { id: 'reset', label: 'Reset Layout', icon: 'refresh', shortcut: keyHint('layout.reset') || undefined, run: () => layout.reset() },
    { id: 'file-save', label: 'Save Layout to File…', icon: 'arrowDown', run: () => void saveLayoutToFile() },
    { id: 'file-load', label: 'Load Layout from File…', icon: 'arrowUp', run: loadLayoutFromFile },
    { id: 'factory', label: 'Revert Factory Settings…', icon: 'warning', run: revertLayoutsToFactory },
    { id: 'max', label: layout.dock.maximized ? 'Restore Maximized Panel' : 'Maximize Panel', icon: 'fullscreen', shortcut: keyHint('layout.maximize') || undefined, run: () => runCommand('layout.maximize') },
    { id: 'sep2', label: '', separator: true },
    { id: 'ai', label: layout.isOpen('ai') ? 'Hide AI Panel' : 'Show AI Panel', icon: 'sparkles', shortcut: keyHint('view.ai') || undefined, run: () => runCommand('view.ai') },
    { id: 'history', label: layout.isOpen('history') ? 'Hide Undo History' : 'Show Undo History', icon: 'undo', shortcut: keyHint('view.history') || undefined, run: () => runCommand('view.history') },
    { id: 'build', label: 'Build Settings…', icon: 'layers', shortcut: keyHint('view.build') || undefined, run: () => runCommand('view.build') },
    { id: 'profiler', label: layout.isOpen('profiler') ? 'Hide Profiler' : 'Show Profiler', icon: 'gauge', shortcut: keyHint('view.profiler') || undefined, run: () => runCommand('view.profiler') },
    { id: 'frame-debugger', label: layout.isOpen('frameDebugger') ? 'Hide Frame Debugger' : 'Show Frame Debugger', icon: 'frames', shortcut: keyHint('view.frame-debugger') || undefined, run: () => runCommand('view.frame-debugger') },
    { id: 'particles', label: layout.isOpen('particles') ? 'Hide Particle Editor' : 'Show Particle Editor', icon: 'particles', shortcut: keyHint('view.particles') || undefined, run: () => runCommand('view.particles') },
    { id: 'manage', label: 'Manage Layouts…', icon: 'settings', run: () => openSettings('layout') },
  );
  return items;
}

/** Editor header: project, workspace tabs, play transport, palette, AI toggle. */
export function TopBar({ projectName }: { projectName: string }) {
  const view = useWorkspace((s) => s.view);
  const projectId = useWorkspace((s) => s.projectId);
  const setView = useWorkspace((s) => s.setView);
  const aiOpen = useWorkspace((s) => s.aiOpen);
  const closeProject = useWorkspace((s) => s.closeProject);
  const logout = useAuth((s) => s.logout);
  const prism = useAuth((s) => s.prism);
  const prismSignOut = useAuth((s) => s.prismSignOut);
  const playState = usePlay((s) => s.state);
  const play = usePlay((s) => s.play);
  const togglePause = usePlay((s) => s.togglePause);
  const stop = usePlay((s) => s.stop);
  const preset = useLayout((s) => s.preset);
  const [socket, setSocket] = useState<SocketStatus>('idle');
  const [tabMenu, setTabMenu] = useState<{ x: number; y: number; view: string } | null>(null);
  const [exporting, setExporting] = useState(false);
  useShortcutVersion();
  const paletteKey = useKeyHint('palette.open');

  useEffect(() => eventSocket.onStatus(setSocket), []);
  // The site's owner (signed in with that account) gets the control panel, on the website.
  const [controlUrl, setControlUrl] = useState<string | null>(null);
  useEffect(() => {
    if (WEBSITE || !prism) return;
    void api
      .get<{ allowed: boolean; url: string | null }>('/auth/control')
      .then((r) => setControlUrl(r.allowed ? r.url : null))
      .catch(() => undefined);
  }, [prism]);

  const running = playState !== 'stopped';
  const titled = (label: string, id: string) => (keyHint(id) ? `${label} (${keyHint(id)})` : label);

  return (
    <header className="topbar">
      <div className="topbar-left">
        <MenuButton
          className="project-btn"
          title="Project"
          items={() => [
            { id: 'h', label: projectName, heading: true },
            { id: 'palette', label: 'Command palette', icon: 'command', shortcut: keyHint('palette.open') || undefined, run: () => openSearch('palette') },
            { id: 'search', label: 'Search everything', icon: 'search', shortcut: keyHint('search.open') || undefined, run: () => openSearch('search') },
            { id: 'settings', label: 'Settings', icon: 'settings', shortcut: keyHint('view.settings') || undefined, run: () => openSettings('shortcuts') },
            { id: 'keys', label: 'Keyboard shortcuts', icon: 'command', shortcut: keyHint('view.shortcuts') || undefined, run: () => openSettings('shortcuts') },
            { id: 'sep', label: '', separator: true },
            { id: 'close', label: 'Close project', icon: 'x', run: closeProject },
            { id: 'logout', label: prism ? 'Lock' : 'Sign out', icon: 'logout', run: () => void logout() },
            ...(controlUrl ? [{ id: 'control', label: 'Control panel', icon: 'settings' as const, run: () => void window.open(controlUrl, '_blank', 'noopener') }] : []),
            ...(prism ? [{ id: 'prism-signout', label: 'Sign out of Prism Engine Cloud account', icon: 'logout' as const, run: () => void prismSignOut() }] : []),
          ]}
        >
          <span className="brand-mark" aria-hidden="true" />
          <span className="project-name">{projectName}</span>
          <Icon name="chevronDown" size={12} className="muted" />
        </MenuButton>

        <nav className="topbar-tabs" role="tablist" aria-label="Workspace">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={view === tab.id}
              className={`topbar-tab${view === tab.id ? ' is-active' : ''}`}
              title={`${titled(tab.label, tabCommandId(tab.id))} — drag to split or float`}
              data-tab={tab.id}
              {...panelDragProps(tab.id)}
              onClick={() => runCommand(tabCommandId(tab.id))}
              onContextMenu={(e) => {
                e.preventDefault();
                setTabMenu({ x: e.clientX, y: e.clientY, view: tab.id });
              }}
            >
              <Icon name={tab.icon} size={14} />
              <span>{tab.label}</span>
              {tab.id === 'play' && running && <span className={`live-dot is-${playState}`} />}
            </button>
          ))}
        </nav>
      </div>

      <div className="transport" role="group" aria-label="Play controls">
        <button
          type="button"
          className={`transport-btn is-play${playState === 'playing' || playState === 'starting' ? ' is-on' : ''}`}
          title={titled('Play', 'play.toggle')}
          aria-label="Play"
          onClick={play}
        >
          <Icon name="play" size={14} />
        </button>
        <button
          type="button"
          className={`transport-btn${playState === 'paused' ? ' is-on' : ''}`}
          title={titled('Pause', 'play.pause')}
          aria-label="Pause"
          disabled={!running}
          onClick={togglePause}
        >
          <Icon name="pause" size={14} />
        </button>
        <button type="button" className="transport-btn" title={titled('Stop', 'play.stop')} aria-label="Stop" disabled={!running} onClick={stop}>
          <Icon name="stop" size={13} />
        </button>
      </div>
      <button type="button" className="topbar-export" title="Export the game: Windows, web, server or the Prism Engine Cloud Library" onClick={() => setExporting(true)}>
        <Icon name="layers" size={14} />
        <span>Export</span>
      </button>
      {exporting && <ExportDialog onClose={() => setExporting(false)} />}

      <div className="topbar-right">
        {!WEBSITE && projectId !== null && <CloudSyncButton projectId={projectId} />}
        <button type="button" className="palette-trigger" onClick={() => openSearch('palette')} title={titled('Command palette', 'palette.open')}>
          <Icon name="search" size={13} />
          <span>Search or run a command</span>
          {paletteKey && <kbd className="kbd">{paletteKey.replace(/\+/g, ' ')}</kbd>}
        </button>
        <span className={`conn conn-${socket}`} title={`Live connection: ${socket}`}>
          <span className="conn-dot" />
        </span>
        <MenuButton className="topbar-layout" title="Layout and workspaces" items={layoutMenu}>
          <Icon name="layers" size={14} />
          <span>{WORKSPACE_PRESETS.find((p) => p.id === preset)?.label ?? 'Layout'}</span>
          <Icon name="chevronDown" size={11} className="muted" />
        </MenuButton>
        <button
          type="button"
          className={`topbar-icon${view === 'settings' ? ' is-active' : ''}`}
          title={titled('Settings', 'view.settings')}
          aria-label="Settings"
          data-tab="settings"
          {...panelDragProps('settings')}
          onClick={() => setView('settings')}
        >
          <Icon name="settings" size={16} />
        </button>
        <button
          type="button"
          className={`ai-toggle${aiOpen ? ' is-on' : ''}`}
          title={titled('Toggle AI panel', 'view.ai')}
          onClick={() => runCommand('view.ai')}
        >
          <Icon name="sparkles" size={14} />
          <span>AI</span>
        </button>
      </div>
      {tabMenu && <ContextMenu x={tabMenu.x} y={tabMenu.y} items={viewDockMenu(tabMenu.view)} onClose={() => setTabMenu(null)} />}
    </header>
  );
}
